import axios from "axios";
import { tokenService } from "./token.service";

/**
 * =========================================================
 * BLUPAY API CLIENT (CRA VERSION)
 * =========================================================
 * Base axios instance used across all services.
 * Works with Create React App (.env REACT_APP_* variables).
 */

const API_BASE_URL =
  process.env.REACT_APP_BLUPAY_API_BASE_URL ||
  "http://127.0.0.1:5000/api/v1";

/**
 * =========================================================
 * AXIOS INSTANCE
 * =========================================================
 */
export const blupayApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});

/**
 * =========================================================
 * REQUEST INTERCEPTOR
 * =========================================================
 */
blupayApi.interceptors.request.use(
  (config) => {
    const token = tokenService.getAccessToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * =========================================================
 * SESSION-EXPIRED HOOK
 * =========================================================
 */
let onSessionExpired = () => {};

export function setOnSessionExpired(callback) {
  onSessionExpired =
    typeof callback === "function"
      ? callback
      : () => {};
}

/**
 * =========================================================
 * REFRESH QUEUE
 * =========================================================
 */
let isRefreshing = false;
let pendingQueue = [];

function processQueue(error, newAccessToken) {
  pendingQueue.forEach(
    ({ resolve, reject, config }) => {
      if (error) {
        reject(error);
        return;
      }

      config.headers = config.headers || {};
      config.headers.Authorization =
        `Bearer ${newAccessToken}`;

      resolve(blupayApi(config));
    }
  );

  pendingQueue = [];
}

/**
 * =========================================================
 * RESPONSE INTERCEPTOR
 * =========================================================
 */
blupayApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    const isUnauthorized =
      response?.status === 401;

    const isAuthEndpoint =
      config?.url?.includes("/auth/");

    const alreadyRetried =
      config?._retry;

    if (
      !isUnauthorized ||
      isAuthEndpoint ||
      alreadyRetried
    ) {
      return Promise.reject(error);
    }

    const refreshToken =
      tokenService.getRefreshToken();

    if (!refreshToken) {
      tokenService.clearTokens();
      onSessionExpired();

      return Promise.reject(error);
    }

    config._retry = true;

    if (isRefreshing) {
      return new Promise(
        (resolve, reject) => {
          pendingQueue.push({
            resolve,
            reject,
            config,
          });
        }
      );
    }

    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {
          refresh_token: refreshToken,
        }
      );

      tokenService.setTokens(
        data.access_token,
        data.refresh_token
      );

      processQueue(
        null,
        data.access_token
      );

      config.headers =
        config.headers || {};

      config.headers.Authorization =
        `Bearer ${data.access_token}`;

      return blupayApi(config);
    } catch (refreshError) {
      processQueue(
        refreshError,
        null
      );

      tokenService.clearTokens();
      onSessionExpired();

      return Promise.reject(
        refreshError
      );
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * =========================================================
 * BLUPAY API ERROR
 * =========================================================
 */
export class BluPayApiError extends Error {
  constructor({
    message,
    code = "REQUEST_FAILED",
    action = null,
    retryable = false,
    requestId = null,
    status = null,
    fields = null,
    fieldReasons = null,
    reason = null,
    reasonValues = null,
    originalError = null,
  }) {
    super(
      message ||
        "We couldn't complete your request."
    );

    this.name = "BluPayApiError";

    this.code = code;
    this.action = action;
    this.retryable =
      Boolean(retryable);

    this.requestId = requestId;
    this.status = status;
    this.fields = fields;
    this.fieldReasons = fieldReasons;

    /**
     * Translation key for `message` and its placeholders
     * (docs/standards/i18n.md). Choose copy by `reason`,
     * never by matching English text.
     */
    this.reason = reason;
    this.reasonValues = reasonValues;

    /**
     * Debugging only.
     *
     * Never render originalError directly
     * to a customer.
     */
    this.originalError = originalError;
  }
}

/**
 * =========================================================
 * VALIDATION HELPERS
 * =========================================================
 */

function normalizeFieldName(location) {
  if (!Array.isArray(location)) {
    return null;
  }

  const ignoredParts = new Set([
    "body",
    "query",
    "path",
    "header",
    "cookie",
  ]);

  const meaningfulParts =
    location.filter(
      (part) =>
        !ignoredParts.has(String(part))
    );

  if (!meaningfulParts.length) {
    return null;
  }

  return meaningfulParts.join(".");
}

function normalizeValidationErrors(
  detail
) {
  if (!Array.isArray(detail)) {
    return {
      message:
        "Some information needs your attention.",
      fields: null,
    };
  }

  const fields = {};

  const messages = detail
    .map((item) => {
      const message =
        item?.msg ||
        item?.message ||
        "Invalid value";

      const fieldName =
        normalizeFieldName(
          item?.loc
        );

      if (fieldName) {
        fields[fieldName] =
          message;
      }

      return message;
    })
    .filter(Boolean);

  return {
    message:
      messages.length === 1
        ? messages[0]
        : "Some information needs your attention.",

    fields:
      Object.keys(fields).length
        ? fields
        : null,
  };
}

/**
 * The backend's 422 `fields` map is `{name: {reason, message}}`,
 * already in BluPay's words; prefer it over Pydantic's `msg`.
 */
function fieldMessages(fields) {
  return mapFields(fields, "message");
}

function fieldReasons(fields) {
  return mapFields(fields, "reason");
}

function mapFields(fields, key) {
  if (!fields || typeof fields !== "object") {
    return null;
  }

  const mapped = Object.fromEntries(
    Object.entries(fields)
      .filter(([, value]) => typeof value?.[key] === "string")
      .map(([name, value]) => [name, value[key]])
  );

  return Object.keys(mapped).length ? mapped : null;
}

/**
 * =========================================================
 * SAFE FALLBACKS
 * =========================================================
 */

function getFallbackMessage(status) {
  switch (status) {
    case 400:
      return (
        "We couldn't process that request. " +
        "Check the information and try again."
      );

    case 401:
      return (
        "Your session has expired. " +
        "Please sign in again."
      );

    case 403:
      return (
        "You can't complete this action right now."
      );

    case 404:
      return (
        "We couldn't find what you were looking for."
      );

    case 409:
      return (
        "This request can no longer be completed in its current state."
      );

    case 422:
      return (
        "Some information needs your attention."
      );

    case 429:
      return (
        "You've made too many requests. " +
        "Please try again shortly."
      );

    case 500:
    case 502:
    case 503:
    case 504:
      return (
        "We're having trouble completing this request right now. " +
        "Please try again shortly."
      );

    default:
      return (
        "We couldn't complete your request."
      );
  }
}

function getRequestId(
  data,
  response
) {
  return (
    data?.request_id ||
    response?.headers?.[
      "x-request-id"
    ] ||
    null
  );
}

/**
 * =========================================================
 * ERROR NORMALIZER
 * =========================================================
 *
 * Every service should throw the value returned from this
 * function rather than exposing a raw Axios response.
 */
export function getApiError(error) {
  /**
   * Avoid normalizing twice.
   */
  if (
    error instanceof BluPayApiError
  ) {
    return error;
  }

  const normalized =
    normalizeApiError(error);

  const data =
    error?.response?.data;

  if (
    typeof data?.reason === "string"
  ) {
    normalized.reason = data.reason;
    normalized.reasonValues =
      data.reason_values || null;
  }

  return normalized;
}

function normalizeApiError(error) {

  const response =
    error?.response;

  const data =
    response?.data;

  const status =
    response?.status ?? null;

  /**
   * -------------------------------------------------------
   * NETWORK / TIMEOUT
   * -------------------------------------------------------
   */
  if (!response) {
    const isTimeout =
      error?.code === "ECONNABORTED" ||
      String(
        error?.message || ""
      )
        .toLowerCase()
        .includes("timeout");

    if (isTimeout) {
      return new BluPayApiError({
        code: "REQUEST_TIMEOUT",
        message:
          "This request is taking longer than expected.",
        action:
          "Please try again.",
        retryable: true,
        originalError: error,
      });
    }

    return new BluPayApiError({
      code: "NETWORK_ERROR",
      message:
        "We couldn't connect to BluPay. Check your internet connection and try again.",
      action:
        "Please try again once you're connected.",
      retryable: true,
      originalError: error,
    });
  }

  /**
   * -------------------------------------------------------
   * FASTAPI VALIDATION
   * -------------------------------------------------------
   */
  if (
    Array.isArray(data?.detail)
  ) {
    const validation =
      normalizeValidationErrors(
        data.detail
      );

    return new BluPayApiError({
      code:
        data?.error_code ||
        "VALIDATION_ERROR",

      message:
        data?.message ||
        validation.message,

      action:
        data?.action ||
        "Check the highlighted information and try again.",

      retryable:
        typeof data?.retryable ===
        "boolean"
          ? data.retryable
          : true,

      requestId: getRequestId(
        data,
        response
      ),

      status,

      fields:
        fieldMessages(data?.fields) ||
        validation.fields,

      fieldReasons:
        fieldReasons(data?.fields),

      originalError: error,
    });
  }

  /**
   * -------------------------------------------------------
   * NEW BLUPAY CONTRACT
   * -------------------------------------------------------
   *
   * {
   *   detail,
   *   message,
   *   error_code,
   *   action,
   *   retryable,
   *   request_id
   * }
   */
  if (
    data &&
    typeof data === "object" &&
    (
      data.message ||
      data.error_code ||
      data.action ||
      data.request_id
    )
  ) {
    return new BluPayApiError({
      code:
        data.error_code ||
        "REQUEST_FAILED",

      message:
        data.message ||
        (
          typeof data.detail ===
          "string"
            ? data.detail
            : null
        ) ||
        getFallbackMessage(status),

      action:
        data.action || null,

      retryable:
        typeof data.retryable ===
        "boolean"
          ? data.retryable
          : status >= 500,

      requestId: getRequestId(
        data,
        response
      ),

      status,

      fields:
        data.fields || null,

      originalError: error,
    });
  }

  /**
   * -------------------------------------------------------
   * LEGACY BLUPAY CONTRACT
   * -------------------------------------------------------
   */
  if (
    typeof data?.detail ===
    "string"
  ) {
    return new BluPayApiError({
      code:
        data?.error_code ||
        "REQUEST_FAILED",

      message:
        data.detail ||
        getFallbackMessage(status),

      action:
        data?.action || null,

      retryable:
        typeof data?.retryable ===
        "boolean"
          ? data.retryable
          : status >= 500,

      requestId: getRequestId(
        data,
        response
      ),

      status,

      fields:
        data?.fields || null,

      originalError: error,
    });
  }

  /**
   * FastAPI can sometimes put structured
   * error metadata inside detail.
   */
  if (
    data?.detail &&
    typeof data.detail === "object"
  ) {
    const detail =
      data.detail;

    return new BluPayApiError({
      code:
        detail.error_code ||
        detail.code ||
        data?.error_code ||
        "REQUEST_FAILED",

      message:
        detail.message ||
        getFallbackMessage(status),

      action:
        detail.action ||
        data?.action ||
        null,

      retryable:
        typeof detail.retryable ===
        "boolean"
          ? detail.retryable
          : typeof data?.retryable ===
            "boolean"
            ? data.retryable
            : status >= 500,

      requestId:
        detail.request_id ||
        getRequestId(
          data,
          response
        ),

      status,

      fields:
        detail.fields ||
        data?.fields ||
        null,

      originalError: error,
    });
  }

  /**
   * -------------------------------------------------------
   * UNKNOWN SERVER RESPONSE
   * -------------------------------------------------------
   *
   * Deliberately do not return arbitrary response.data.
   *
   * A provider or unexpected backend response could contain
   * implementation details that should never automatically
   * become UI text.
   */
  return new BluPayApiError({
    code:
      status >= 500
        ? "SERVICE_ERROR"
        : "REQUEST_FAILED",

    message:
      getFallbackMessage(status),

    action:
      status >= 500
        ? "Please try again shortly."
        : null,

    retryable:
      status >= 500,

    requestId: getRequestId(
      data,
      response
    ),

    status,
    originalError: error,
  });
}